import type { OrgUnit } from '@/types/prioritization/enterpriseIdentity';
import {
  filterOrgUnitsForCycleScope,
  getCanonicalWorkspaceOrgRoot,
  resolveParentId,
} from '@/lib/orgUnitTree';
import type { OrgPathNode } from '../types';

export function toOrgUnitArray(units: OrgUnit[] | Map<string, OrgUnit> | null | undefined): OrgUnit[] {
  if (!units) return [];
  if (Array.isArray(units)) return units;
  if (units instanceof Map) return Array.from(units.values());
  return [];
}

function childCount(units: OrgUnit[], unitId: string, idSet: Set<string>): number {
  return units.filter((u) => resolveParentId(u, idSet) === unitId).length;
}

/**
 * Build flat org path nodes scoped to the cycle anchor subtree.
 */
export function buildOrgPathNodes(
  allUnits: OrgUnit[] | Map<string, OrgUnit>,
  workspaceId: string | null | undefined,
  anchorOrgUnitId: string | undefined,
): { nodes: OrgPathNode[]; anchor: OrgPathNode | null; maxRelativeLevel: number } {
  const scoped = filterOrgUnitsForCycleScope(toOrgUnitArray(allUnits), workspaceId);
  if (scoped.length === 0) {
    return { nodes: [], anchor: null, maxRelativeLevel: 0 };
  }

  const idSet = new Set(scoped.map((u) => u.id));
  // The cycle's own anchor selection wins. Otherwise the anchor is simply the
  // topmost node — resolved by the one shared helper rather than a second copy
  // of the rule here, which used to require `type === 'organization'` and fall
  // through to `scoped[0]` (an arbitrary unit) whenever no node carried that type.
  const anchorUnit =
    (anchorOrgUnitId && scoped.find((u) => u.id === anchorOrgUnitId)) ??
    (workspaceId ? getCanonicalWorkspaceOrgRoot(scoped, workspaceId) : null) ??
    scoped.find((u) => !resolveParentId(u, idSet)) ??
    scoped[0];

  if (!anchorUnit) {
    return { nodes: [], anchor: null, maxRelativeLevel: 0 };
  }

  const descendants = new Set<string>();
  const collectDescendants = (id: string) => {
    descendants.add(id);
    scoped.forEach((u) => {
      const parentId = resolveParentId(u, idSet);
      if (parentId === id) collectDescendants(u.id);
    });
  };
  collectDescendants(anchorUnit.id);

  const subtree = scoped.filter((u) => descendants.has(u.id));

  // Levels follow the live parent chain, not stored `depth`. Reparenting in
  // Org Hierarchy updates parentId immediately but historically left depth stale
  // — using depth here made Design Paths look flat after hierarchy edits.
  const parentInSubtree = new Map<string, string | null>();
  for (const unit of subtree) {
    const parentId = resolveParentId(unit, idSet);
    parentInSubtree.set(
      unit.id,
      parentId && descendants.has(parentId) ? parentId : null,
    );
  }

  const relativeLevelOf = (id: string): number => {
    if (id === anchorUnit.id) return 0;
    let level = 0;
    let cur: string | null = id;
    const seen = new Set<string>();
    while (cur && cur !== anchorUnit.id) {
      if (seen.has(cur)) break;
      seen.add(cur);
      const parentId: string | null = parentInSubtree.get(cur) ?? null;
      if (!parentId) break;
      level += 1;
      cur = parentId;
      if (level > subtree.length) break;
    }
    return level;
  };

  const nodes: OrgPathNode[] = subtree.map((unit) => {
    const parentId = parentInSubtree.get(unit.id) ?? null;
    const relativeLevel = relativeLevelOf(unit.id);
    const title =
      unit.type === 'team'
        ? unit.description ?? unit.type
        : unit.type.replace(/_/g, ' ');

    return {
      id: unit.id,
      name: unit.displayName,
      title,
      relativeLevel,
      parentId,
      isLeaf: childCount(subtree, unit.id, idSet) === 0,
    };
  });

  const anchor =
    nodes.find((n) => n.id === anchorUnit.id) ??
    ({
      id: anchorUnit.id,
      name: anchorUnit.displayName,
      title: anchorUnit.type,
      relativeLevel: 0,
      parentId: null,
      isLeaf: childCount(subtree, anchorUnit.id, idSet) === 0,
    } satisfies OrgPathNode);

  const maxRelativeLevel = nodes.reduce((max, n) => Math.max(max, n.relativeLevel), 0);

  return { nodes, anchor, maxRelativeLevel };
}

export function nodesAtRelativeLevel(nodes: OrgPathNode[], level: number): OrgPathNode[] {
  return nodes.filter((n) => n.relativeLevel === level);
}

export function nodesBelowAnchor(nodes: OrgPathNode[]): OrgPathNode[] {
  return nodes.filter((n) => n.relativeLevel > 0);
}
